/**
 * Stable 32-bit hash from a string.
 * @param {string} str
 * @returns {number}
 */
export function hashId(str) {
	let h = 2166136261;
	const s = String(str || '');
	for (let i = 0; i < s.length; i++) {
		h ^= s.charCodeAt(i);
		h = Math.imul(h, 16777619);
	}
	return h >>> 0;
}

/**
 * Seeded 0–1 random.
 * @param {number} seed
 * @returns {number}
 */
function rand(seed) {
	const x = Math.sin(seed) * 10000;
	return x - Math.floor(x);
}

/**
 * Sample count for a time range.
 * @param {string} timePeriod
 * @returns {number}
 */
function sampleCount(timePeriod) {
	if (timePeriod === 'day') return 24;
	if (timePeriod === 'month') return 30;
	if (timePeriod === 'custom') return 16;
	return 12;
}

/**
 * Mock curval + sparkline for a point or card id.
 * @param {string} id
 * @param {string} timePeriod
 * @param {{ unit?: string, base?: number, amp?: number }} [opts]
 * @returns {{ value: number, unit: string, sparkline: number[], series: number[], delta: number, deltaLabel: string }}
 */
export function mockPointData(id, timePeriod, opts) {
	const options = opts || {};
	const seed = hashId((id || 'point') + ':' + (options.refreshKey || 0));
	const n = sampleCount(timePeriod);
	const base = options.base != null ? options.base : 40 + (seed % 50);
	const amp = options.amp != null ? options.amp : 4 + (seed % 8);
	const series = [];
	for (let i = 0; i < n; i++) {
		const wobble = Math.sin((i + (seed % 7)) / 2.4) * amp;
		const noise = (rand(seed + i * 17) - 0.5) * amp * 0.4;
		series.push(Math.round((base + wobble + noise) * 10) / 10);
	}
	const value = series[series.length - 1];
	const prev = series[0];
	const delta = Math.round((value - prev) * 10) / 10;
	const unit = options.unit || '';
	return {
		value,
		unit,
		sparkline: series,
		series,
		delta,
		deltaLabel: (delta >= 0 ? '+' : '') + delta + (unit ? ' ' + unit : '')
	};
}

/**
 * Category labels for the selected range.
 * @param {string} timePeriod
 * @param {number} count
 * @returns {string[]}
 */
export function mockAxisLabels(timePeriod, count) {
	const n = count || sampleCount(timePeriod);
	const labels = [];
	if (timePeriod === 'day') {
		for (let i = 0; i < n; i++) labels.push((i < 10 ? '0' : '') + i + ':00');
		return labels;
	}
	const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
	const now = new Date();
	for (let i = n - 1; i >= 0; i--) {
		const d = new Date(now.getTime());
		if (timePeriod === 'month') d.setDate(d.getDate() - i);
		else d.setDate(d.getDate() - i);
		labels.push(months[d.getMonth()] + ' ' + d.getDate());
	}
	return labels;
}

/**
 * Mock donut slices.
 * @param {string} id
 * @returns {Array<{ name: string, value: number }>}
 */
export function mockDonutSlices(id) {
	const seed = hashId(id);
	const rows = [
		{ name: 'Air Handling Units', value: 82 },
		{ name: 'Chillers', value: 76 },
		{ name: 'Boilers', value: 74 },
		{ name: 'Rooftop Units', value: 71 },
		{ name: 'Cooling Towers', value: 69 },
		{ name: 'Pumps', value: 66 },
		{ name: 'Other', value: 73 }
	];
	return rows.map((row, i) => ({
		name: row.name,
		value: Math.max(40, Math.min(99, row.value + ((seed >> (i * 3)) % 5) - 2))
	}));
}

/**
 * Mock ranked rows for bar / table cards.
 * @param {string} id
 * @param {number} [count]
 * @returns {Array<{ name: string, value: number, status: string }>}
 */
export function mockRankRows(id, count) {
	const n = count || 6;
	const rows = [
		{ name: 'AHU-1', value: 92, hours: 156 },
		{ name: 'AHU-2', value: 89, hours: 151 },
		{ name: 'AHU-3', value: 45, hours: 76, warn: true },
		{ name: 'Chiller-1', value: 78, hours: 132 },
		{ name: 'Chiller-2', value: 81, hours: 138 },
		{ name: 'VAV Boxes', value: 85, hours: 612 }
	];
	return rows.slice(0, n);
}

/**
 * Mock work order rows.
 * @param {string} id
 * @returns {Array<object>}
 */
export function mockWorkOrders(id) {
	return [
		{ id: 'WO-48291', title: 'Chiller-01 Not Cooling', place: '1st Floor Mechanical Room', priority: 'High', age: '2 min ago', assignee: 'Mike Johnson' },
		{ id: 'WO-48264', title: 'AHU-7 Filter Replacement', place: '2nd Floor North', priority: 'High', age: '18 min ago', assignee: 'Sara Chen' },
		{ id: 'WO-48112', title: 'Boiler-02 Temperature Fluctuation', place: 'Boiler Room', priority: 'Medium', age: '1h ago', assignee: 'Alex Patel' }
	];
}

/**
 * Sample alert rows when the site has none active.
 * @returns {Array<object>}
 */
export function mockAlerts() {
	return [
		{ id: 'm1', title: 'Chiller-01 High Discharge Pressure', place: '1st Floor Mechanical Room', when: '2 min ago', cls: 'is-crit', label: 'Critical', icon: 'mdi-shield-alert' },
		{ id: 'm2', title: 'AHU-7 Filter Pressure High', place: '2nd Floor North', when: '8 min ago', cls: 'is-warn', label: 'Warning', icon: 'mdi-alert' },
		{ id: 'm3', title: 'Boiler-02 Temperature Fluctuation', place: 'Boiler Room', when: '15 min ago', cls: 'is-alert', label: 'Alert', icon: 'mdi-alert' },
		{ id: 'm4', title: 'RTU-5 Maintenance Due', place: 'Roof', when: '30 min ago', cls: 'is-info', label: 'Info', icon: 'mdi-information-variant' },
		{ id: 'm5', title: 'CO2 Level Elevated', place: '2nd Floor East Wing', when: '1h ago', cls: 'is-alert', label: 'Alert', icon: 'mdi-alert' }
	];
}

/**
 * Mock zone tiles for the floor-plan preview.
 * @param {string} id
 * @returns {Array<object>}
 */
export function mockZones(id) {
	const seed = hashId(id);
	const statuses = ['excellent', 'good', 'good', 'fair', 'unoccupied'];
	const tiles = [];
	for (let i = 0; i < 9; i++) {
		const st = statuses[(seed + i) % statuses.length];
		tiles.push({
			id: 101 + i,
			temp: (68 + ((seed >> i) % 8) + (i % 3) * 0.3).toFixed(1),
			rh: 40 + ((seed >> (i + 2)) % 18),
			status: st
		});
	}
	return tiles;
}
